import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORIES, SEVERITIES, STOCK, INV_ICON } from "../lib/categories";
import { PushButton } from "../components/PushButton";
import { MediaGallery } from "../components/MediaGallery";
import { QAPanel } from "../components/QAPanel";
import { CommunityQA } from "../components/CommunityQA";
import { BannerCarousel } from "../components/BannerCarousel";
import { useAppData } from "../context/AppDataContext";
import {
  HeartPulse, AlertTriangle, Calendar, Clock, MapPin, Package, Phone,
  ArrowRight, ShieldCheck, Megaphone, WifiOff, MessageCircle, Building2, GraduationCap, Eye, UserPlus, Stethoscope, Play, Pause,
} from "lucide-react";
import { VideoEmbed } from "../components/VideoEmbed";
import { TrainingRsvp } from "../components/TrainingRsvp";
import { registerTrainingViews } from "../lib/trainingViews";
import { useT } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { ListenButton } from "../components/ListenButton";
import { AudioNote } from "../components/AudioNote";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : "");

const SECTIONS = ["feed", "schedules", "events", "learn"];
const NAV = [
  { k: "feed", label: "News", icon: Megaphone },
  { k: "schedules", label: "Clinics", icon: Clock },
  { k: "events", label: "Events", icon: Calendar },
  { k: "learn", label: "Learn", icon: GraduationCap },
];

function Pill({ cat, label }) {
  const c = CATEGORIES[cat] || CATEGORIES.general;
  const Icon = c.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
      style={{ color: c.color, backgroundColor: `${c.color}15` }}
    >
      <Icon className="w-3.5 h-3.5" /> {label || c.label}
    </span>
  );
}

export default function PublicHome() {
  const [tab, setTab] = useState("feed");
  const [filter, setFilter] = useState("all");
  const [highlightId, setHighlightId] = useState(null);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [alertScrolling, setAlertScrolling] = useState(false);
  const [data, setData] = useState({ announcements: [], inventory: [], schedules: [], events: [], training: [], clinics: [] });
  const { clinics, banners, settings, clinicId, setClinicId, catName } = useAppData();
  const { t } = useT();

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    (async () => {
      const cq = clinicId && clinicId !== "all" ? `?clinic=${clinicId}` : "";
      const [a, i, s, e, t, cl] = await Promise.all([
        api.get(`/announcements${cq}`),
        api.get(`/inventory${cq}`),
        api.get(`/schedules${cq}`),
        api.get(`/events${cq}`),
        api.get(`/public-training`),
        api.get(`/clinics`),
      ]);
      setData({ announcements: a.data, inventory: i.data, schedules: s.data, events: e.data, training: t.data, clinics: cl.data });
      const upd = await registerTrainingViews(t.data);
      if (Object.keys(upd).length) {
        setData((prev) => ({ ...prev, training: prev.training.map((x) => (upd[x.id] != null ? { ...x, views: upd[x.id] } : x)) }));
      }
    })();
  }, [clinicId]);

  const activeClinics = clinics.filter((c) => c.active);
  const waBase = `${process.env.REACT_APP_BACKEND_URL}/api/announcements`;

  const _today = new Date().toISOString().slice(0, 10);
  const emergencies = data.announcements.filter((a) => a.is_emergency && a.title?.trim() && (!a.expires_at || a.expires_at >= _today));
  const feed = filter === "all"
    ? data.announcements
    : data.announcements.filter((a) => a.category === filter);
  const cats = ["all", ...Array.from(new Set(data.announcements.map((a) => a.category)))];
  const clinicName = (id) => data.clinics.find((c) => c.id === id)?.name || "";

  // Deep-link from a push notification: /?announcement=<id> → open feed, scroll & highlight.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("announcement");
    if (!id || data.announcements.length === 0) return;
    if (!data.announcements.some((a) => a.id === id)) return;
    setTab("feed");
    setFilter("all");
    setHighlightId(id);
    const el = document.getElementById(`ann-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setHighlightId(null), 4000);
    window.history.replaceState({}, "", window.location.pathname);
    return () => clearTimeout(t);
  }, [data.announcements]);

  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-line" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
            <HeartPulse className="w-7 h-7 text-critical shrink-0" />
            <span className="flex flex-col leading-none">
              <span className="font-heading font-black text-xl text-clinical tracking-tight">Hutanohwevanhu.org</span>
              <span className="text-[10px] sm:text-[11px] font-semibold text-muted2 tracking-tight mt-0.5">{t("Rural Health Promotion & Telemedicine")}</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {SECTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setTab(s)}
                data-testid={`nav-${s}`}
                className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors ${
                  tab === s ? "bg-clinical text-white" : "text-muted2 hover:bg-line/50"
                }`}
              >
                {t(s === "feed" ? "Announcements" : s === "schedules" ? "Clinics" : s === "events" ? "Events" : "Learn")}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            {activeClinics.length > 0 && (
              <div className="relative hidden sm:block">
                <Building2 className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-clinical pointer-events-none" />
                <select
                  value={clinicId}
                  onChange={(e) => setClinicId(e.target.value)}
                  data-testid="clinic-selector"
                  className="appearance-none border border-line bg-white rounded-full pl-8 pr-7 py-2 text-sm font-medium text-clinical outline-none focus:border-clinical cursor-pointer"
                >
                  <option value="all">{t("All clinics")}</option>
                  {activeClinics.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <PushButton userType="public" variant="light" />
            <Link
              to="/login"
              data-testid="staff-login-btn"
              className="inline-flex items-center gap-1.5 border border-line bg-white text-clinical text-sm font-semibold px-3 py-2 rounded-full hover:bg-line/40 transition-colors"
            >
              <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline">{t("Staff")}</span>
            </Link>
          </div>
        </div>
      </header>

      {offline && (
        <div className="bg-alert text-ink" data-testid="offline-banner">
          <div className="max-w-7xl mx-auto px-5 py-2 flex items-center gap-2 text-sm font-medium">
            <WifiOff className="w-4 h-4" /> {t("You're offline — showing the latest saved updates.")}
          </div>
        </div>
      )}

      {/* Emergency banner */}
      {emergencies.length > 0 && (
        <div className="bg-critical text-white" data-testid="emergency-banner">
          <div className="max-w-7xl mx-auto px-5 py-3 flex items-center gap-3">
            <span className="relative flex h-3 w-3 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
            </span>
            <span className="font-heading font-bold uppercase tracking-wider text-sm shrink-0">{t("Critical Alert")}</span>
            <div className="overflow-hidden flex-1" data-testid="critical-marquee">
              {alertScrolling ? (
                <div className="whitespace-nowrap animate-marquee inline-block"
                     onMouseEnter={(e) => (e.currentTarget.style.animationPlayState = "paused")}
                     onMouseLeave={(e) => (e.currentTarget.style.animationPlayState = "running")}>
                  {[...emergencies, ...emergencies].map((em, i) => (
                    <span key={`${em.id || em.title}-${i}`} className="text-sm mx-10">{em.title} — {em.body}</span>
                  ))}
                </div>
              ) : (
                <div className="text-sm truncate" data-testid="critical-static">
                  <span className="font-semibold">{emergencies[0].title}</span>
                  {emergencies[0].body && <span> — {emergencies[0].body}</span>}
                  {emergencies.length > 1 && <span className="text-white/70"> · +{emergencies.length - 1} more</span>}
                </div>
              )}
            </div>
            <button
              type="button"
              data-testid="critical-scroll-toggle"
              onClick={() => setAlertScrolling((s) => !s)}
              aria-label={alertScrolling ? "Pause scrolling" : "Play scrolling"}
              className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
            >
              {alertScrolling ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-5 py-10 pb-28 md:pb-10">
        {tab === "feed" && (
          <>
            {banners.length > 0 && (
              <div className="mb-10"><BannerCarousel banners={banners} /></div>
            )}
            <div className="mb-10">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-calm">{t("Community Health Network")}</p>
              <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-ink mt-3 max-w-3xl text-balance">
                {t("Real-time health updates for our remote communities.")}
              </h1>
              <p className="text-muted2 mt-4 max-w-xl leading-relaxed">
                {t("Outbreak alerts, immunisation drives, antivenom availability, clinic schedules and outreach services — all in one place.")}
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-7">
                <Link
                  to="/login?mode=login"
                  data-testid="hero-patient-login-cta"
                  className="inline-flex items-center gap-2 bg-clinical text-white font-semibold px-6 py-3 rounded-full text-base hover:bg-clinicaldark transition-colors"
                >
                  <UserPlus className="w-5 h-5" /> {t("Patient Login")}
                </Link>
                <Link
                  to="/login?mode=register-public"
                  data-testid="hero-patient-register-cta"
                  className="inline-flex items-center gap-2 border-2 border-clinical text-clinical font-semibold px-6 py-3 rounded-full text-base hover:bg-clinical/5 transition-colors"
                >
                  <UserPlus className="w-5 h-5" /> {t("Create Patient Account")} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/login"
                  data-testid="hero-staff-cta"
                  className="inline-flex items-center gap-2 border border-line bg-white text-clinical font-semibold px-6 py-3 rounded-full text-base hover:bg-line/40 transition-colors"
                >
                  <ShieldCheck className="w-5 h-5" /> {t("Admin Portal - Login")} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/login"
                  data-testid="hero-clinician-cta"
                  className="inline-flex items-center gap-2 border border-line bg-white text-clinical font-semibold px-6 py-3 rounded-full text-base hover:bg-line/40 transition-colors"
                >
                  <Stethoscope className="w-5 h-5" /> {t("Clinician Portal - Login")} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <p className="text-xs text-muted2 mt-3">{t("New here? Create a free patient account to book virtual consultations and get your appointment code.")}</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8" data-testid="category-filters">
              {cats.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`filter-${c}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    filter === c ? "bg-clinical text-white border-clinical" : "bg-white border-line text-muted2 hover:border-clinical"
                  }`}
                >
                  {c === "all" ? t("All") : t(CATEGORIES[c]?.label || c)}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="announcement-grid">
              {feed.map((a, idx) => {
                const sev = SEVERITIES[a.severity] || SEVERITIES.info;
                const big = idx === 0;
                return (
                  <article
                    key={a.id}
                    id={`ann-${a.id}`}
                    data-testid="announcement-card"
                    className={`bg-white border border-line rounded-xl p-6 hover:-translate-y-1 hover:shadow-md transition-all duration-300 animate-fade-up ${
                      big ? "md:col-span-8" : "md:col-span-4"
                    } ${a.is_emergency ? "ring-2 ring-critical/30" : ""} ${highlightId === a.id ? "ring-2 ring-clinical shadow-lg" : ""}`}
                    style={{ animationDelay: `${idx * 60}ms` }}
                  >
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <Pill cat={a.category} label={catName("announcement", a.category)} />
                      <span
                        className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                        style={{ color: sev.color, backgroundColor: sev.bg }}
                      >
                        {a.is_emergency ? t("Emergency") : t(sev.label)}
                      </span>
                    </div>
                    <h3 className={`font-heading font-bold tracking-tight text-ink ${big ? "text-2xl" : "text-xl"}`}>
                      {t(a.title)}
                    </h3>
                    <p className={`text-muted2 mt-3 leading-relaxed ${big ? "" : "text-sm"}`}>{t(a.body)}</p>
                    {a.audio_url
                      ? <AudioNote src={a.audio_url} testid="announcement-audio" />
                      : <div className="mt-2"><ListenButton text={`${a.title}. ${a.body}`} testid="announcement-listen-btn" /></div>}
                    {a.media?.length > 0 && <div className="mt-4"><MediaGallery media={a.media} /></div>}
                    {a.whatsapp_enabled && settings.whatsapp_active && (
                      <a
                        href={`${waBase}/${a.id}/whatsapp`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="whatsapp-btn"
                        className="mt-4 inline-flex items-center gap-2 bg-[#25D366] text-white text-sm font-semibold px-4 py-2 rounded-full hover:opacity-90 transition-opacity"
                      >
                        <MessageCircle className="w-4 h-4" /> {t("Contact via WhatsApp")}
                      </a>
                    )}
                    <div className="mt-5 pt-4 border-t border-line flex items-center justify-between text-xs text-muted2">
                      <span>{a.created_by}</span>
                      <span>{new Date(a.created_at).toLocaleDateString()}</span>
                    </div>
                    <QAPanel announcementId={a.id} />
                  </article>
                );
              })}
              {feed.length === 0 && <p className="text-muted2 col-span-12">{t("No announcements yet.")}</p>}
            </div>
          </>
        )}

        {tab === "schedules" && (
          <section>
            <h2 className="font-heading font-bold text-3xl tracking-tight text-ink flex items-center gap-3">
              <Building2 className="w-7 h-7 text-clinical" /> {t("Our Clinics")}
            </h2>
            <p className="text-muted2 mt-2 mb-6">{t("Where to go and who to call.")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-testid="clinic-directory">
              {activeClinics.map((c) => {
                const mapHref = c.map_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([c.name, c.location].filter(Boolean).join(" "))}`;
                const photoSrc = c.photo ? (c.photo.startsWith("http") ? c.photo : `${process.env.REACT_APP_BACKEND_URL}${c.photo}`) : "";
                return (
                <div key={c.id} data-testid="clinic-info-card" className="bg-white border border-line rounded-xl overflow-hidden hover:shadow-md transition-shadow flex flex-col">
                  {photoSrc && <img src={photoSrc} alt={c.name} data-testid="clinic-photo" className="w-full h-36 object-cover" />}
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><Building2 className="w-5 h-5 text-clinical shrink-0" /> {c.name}</h3>
                    {c.location && <p className="inline-flex items-center gap-1.5 text-sm text-muted2 mt-2"><MapPin className="w-4 h-4 text-clinical" /> {c.location}</p>}
                    {c.phone && <p className="mt-1"><a href={`tel:${c.phone.replace(/\s+/g, "")}`} data-testid="clinic-phone-link" className="inline-flex items-center gap-1.5 text-sm text-clinical font-semibold hover:underline"><Phone className="w-4 h-4" /> {c.phone}</a></p>}
                    {c.description && <p className="text-sm text-muted2 mt-2 leading-relaxed">{t(c.description)}</p>}
                    {c.services?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3" data-testid="clinic-services">
                        {c.services.map((s) => <span key={s} className="text-xs font-semibold bg-clinical/10 text-clinical rounded-full px-2.5 py-1">{s}</span>)}
                      </div>
                    )}
                    <div className="mt-4 flex items-center gap-4">
                      <a href={mapHref} target="_blank" rel="noreferrer" data-testid="clinic-map-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-clinical rounded-full px-4 py-2 w-fit hover:bg-clinicaldark transition-colors">
                        <MapPin className="w-4 h-4" /> {t("Get directions")}
                      </a>
                      <ListenButton testid="clinic-listen-btn"
                        text={[c.name, c.location ? `Location: ${c.location}` : "", c.phone ? `Phone: ${c.phone}` : "", c.description || ""].filter(Boolean).join(". ")} />
                    </div>
                  </div>
                </div>
              );})}
              {activeClinics.length === 0 && <p className="text-muted2">{t("No clinics listed yet.")}</p>}
            </div>
            <h2 className="font-heading font-bold text-3xl tracking-tight text-ink flex items-center gap-3">
              <Clock className="w-7 h-7 text-clinical" /> {t("Clinic Timetables")}
            </h2>
            <p className="text-muted2 mt-2 mb-8">{t("When and where to find each service.")}</p>
            <div className="grid gap-4" data-testid="schedule-grid">
              {data.schedules.map((s) => (
                <div key={s.id} data-testid="schedule-card" className="bg-white border border-line rounded-md p-5 flex flex-wrap items-center gap-x-8 gap-y-2">
                  <h3 className="font-heading font-bold text-lg text-ink min-w-[200px]">{t(s.title)}</h3>
                  <span className="inline-flex items-center gap-1.5 text-sm text-clinical font-medium"><Calendar className="w-4 h-4" /> {s.schedule_type === "dated" ? (s.date || "Date TBC") : (s.day || "—")}</span>

                  <span className="inline-flex items-center gap-1.5 text-sm text-clinical font-medium"><Clock className="w-4 h-4" /> {s.time}</span>
                  <span className="inline-flex items-center gap-1.5 text-sm text-muted2"><MapPin className="w-4 h-4" /> {s.clinic}</span>
                  {s.description && <span className="text-sm text-muted2 basis-full">{t(s.description)}</span>}
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === "events" && (
          <section>
            <h2 className="font-heading font-bold text-3xl tracking-tight text-ink flex items-center gap-3">
              <Calendar className="w-7 h-7 text-clinical" /> {t("Public Health Events")}
            </h2>
            <p className="text-muted2 mt-2 mb-8">{t("Upcoming initiatives and outreach in your area.")}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="event-grid">
              {data.events.map((ev) => (
                <div key={ev.id} data-testid="event-card" className="bg-white border border-line rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                  <div className="bg-clinical text-white p-4">
                    <p className="text-xs uppercase tracking-[0.2em] opacity-80">{new Date(ev.date).toDateString()}</p>
                    <h3 className="font-heading font-bold text-xl mt-1">{t(ev.title)}</h3>
                  </div>
                  <div className="p-5">
                    <Pill cat={ev.category} label={catName("event", ev.category)} />
                    {ev.media?.length > 0 && <div className="mt-3"><MediaGallery media={ev.media} /></div>}
                    <p className="text-sm text-muted2 mt-3 leading-relaxed">{t(ev.description)}</p>
                    {ev.audio_url
                      ? <AudioNote src={ev.audio_url} testid="event-audio" />
                      : <div className="mt-2"><ListenButton text={`${ev.title}. ${ev.description}`} testid="event-listen-btn" /></div>}
                    <p className="inline-flex items-center gap-1.5 text-sm text-clinical font-medium mt-4"><MapPin className="w-4 h-4" /> {ev.location}</p>
                    <QAPanel endpoint={`/events/${ev.id}/questions`} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        {tab === "learn" && (
          <section>
            <h2 className="font-heading font-bold text-3xl tracking-tight text-ink flex items-center gap-3">
              <GraduationCap className="w-7 h-7 text-clinical" /> {t("Learn & Health Education")}
            </h2>
            <p className="text-muted2 mt-2 mb-8">{t("Watch health-education videos and download materials to stay informed.")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="public-training-grid">
              {data.training.length === 0 && <p className="text-muted2">{t("No health-education materials yet. Check back soon.")}</p>}
              {data.training.map((t2) => (
                <div key={t2.id} data-testid="public-training-card" className="bg-white border border-line rounded-xl p-5">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-heading font-bold text-lg text-ink">{t(t2.title)} {t2.date && <span className="text-xs text-muted2 font-normal">· {t2.date}</span>}</h3>
                    <span data-testid="public-training-views" className="inline-flex items-center gap-1 text-xs text-muted2 shrink-0"><Eye className="w-3.5 h-3.5" /> {t2.views || 0}</span>
                  </div>
                  {t2.description && <p className="text-sm text-muted2 mt-1 leading-relaxed">{t(t2.description)}</p>}
                  {t2.video_url && <VideoEmbed url={t2.video_url} testid="public-training-video" />}
                  {t2.materials?.map((m, i) => <a key={i} href={abs(m.url)} target="_blank" rel="noreferrer" className="text-sm text-clinical underline block mt-2">📎 {m.name}</a>)}
                  <TrainingRsvp trainingId={t2.id} counts={t2.rsvp} testid="public-training-rsvp" />
                </div>
              ))}
            </div>
          </section>
        )}
        <CommunityQA />
      </main>

      {/* Mobile bottom tab bar */}
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-line flex"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {NAV.map((n) => {
          const active = tab === n.k;
          return (
            <button
              key={n.k}
              onClick={() => setTab(n.k)}
              data-testid={`bottom-nav-${n.k}`}
              className={`flex-1 flex flex-col items-center gap-1 py-2.5 ${active ? "text-clinical" : "text-muted2"}`}
            >
              <n.icon className="w-5 h-5" />
              <span className="text-[11px] font-semibold">{t(n.label)}</span>
            </button>
          );
        })}
      </nav>

      <footer className="border-t border-line bg-white mt-16">
        <div className="max-w-7xl mx-auto px-5 py-10 flex flex-col sm:flex-row justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 font-heading font-black text-lg text-clinical"><HeartPulse className="w-5 h-5 text-critical" /> Hutanohwevanhu.org</div>
            <p className="text-xs font-semibold text-muted2 mt-1">{t("Rural Health Promotion & Telemedicine")}</p>
            <p className="text-sm text-muted2 mt-2 max-w-sm">{t("Strengthening healthcare delivery in remote and rural regions through real-time community communication.")}</p>
          </div>
        </div>
        <div className="border-t border-line">
          <p className="max-w-7xl mx-auto px-5 py-4 text-xs text-muted2 text-center leading-relaxed" data-testid="copyright">
            © 2026 <a href="https://www.linkedin.com/in/tapiwa-mapunde-b72655423" target="_blank" rel="noopener noreferrer" data-testid="author-linkedin-link" className="font-bold text-clinical hover:underline">Tapiwa Marvin Mapunde</a>. All Rights Reserved.<br />
            Authored and designed by Tapiwa Marvin Mapunde - Master of Science in Global Healthcare Leadership.
          </p>
        </div>
      </footer>
    </div>
  );
}
